import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  makeStyles,
  Typography
} from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { useSnackBar } from 'src/hooks';
import Papa from 'papaparse';
import { CircularProgress } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: 50,
    backgroundColor: theme.palette.grey[50],
    border: '0.5px dashed grey',
    boxShadow: theme.shadows[3],
    borderRadius: 8,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  rejectedFilesDiv: {
    marginTop: '1em',
    padding: theme.spacing(1.5)
  }
}));

interface Props {
  file?: File;
  open?: boolean;
  isLoading?: boolean;
  selectedScopeIds?: number[];
  onConfirm: (ids: number[]) => void;
  onClose: () => void;
}

export const ImportProductCSVModal: FC<Props> = ({
  open = false,
  isLoading = false,
  selectedScopeIds = [],
  onClose,
  onConfirm
}) => {
  const classes = useStyles();
  const snackbar = useSnackBar();

  const [productCSV, setProductCSV] = useState<File>();

  const getProductIdsFromCSV = (val?: File): Promise<number[]> => {
    return new Promise((resolve, reject) => {
      if (!val) {
        const errMsg = 'Invalid file for getProductIdsFromCSV function';
        snackbar.show({ severity: 'error', message: errMsg });
        return resolve([]); // resolve empty so caller can handle
      }

      // Papa.parse is async so we gotta do it promise style
      Papa.parse(val, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const idsArr = result.data
            .map((row: any) => Number(row.product_id ?? row.id))
            .filter((id) => {
              const isValidNumber = !isNaN(id) && id > 0;
              const isNotSelected = !selectedScopeIds.includes(id);
              return isValidNumber && isNotSelected;
            });

          resolve(idsArr);
        },
        error: (err) => reject(err)
      });
    });
  };

  const handleConfirm = async () => {
    const productIds = (await getProductIdsFromCSV(productCSV)) ?? [];
    onConfirm(productIds);
  };

  const onDrop = (v: File[]) => (v?.[0] ? setProductCSV(v?.[0]) : null);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections
  } = useDropzone({
    onDrop,
    accept: {
      'text/csv': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': []
    },
    maxSize: 5242880 // 5mb
  });

  const fileRejectionItems = fileRejections?.map(({ file, errors }) => {
    return (
      <li key={file?.name}>
        {file?.name} - {file?.size} bytes
        <ul>
          {errors.map((e) => (
            <li key={e?.code}>{e?.message}</li>
          ))}
        </ul>
      </li>
    );
  });

  // reset productCSV on reopen
  useEffect(() => {
    setProductCSV(undefined);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { width: '80vw' }
      }}
    >
      <DialogTitle>Select Products via CSV</DialogTitle>
      <DialogContent>
        <div {...getRootProps()} className={classes.root}>
          <input {...getInputProps()} />
          {productCSV && !isLoading ? (
            <div
              style={{
                display: 'flex',
                textAlign: 'center',
                flexDirection: 'column'
              }}
            >
              <Typography>{productCSV.name}</Typography>
              <Typography variant="subtitle1">
                {(productCSV.size / 1024).toFixed(1)} KB
              </Typography>
            </div>
          ) : null}

          {!productCSV && !isLoading ? (
            <>
              <Typography variant="h5">
                {isDragActive ? 'Release Mouse' : 'Drag/Upload CSV file here'}
              </Typography>
              <AttachFileIcon />
            </>
          ) : null}
        </div>

        <FormHelperText style={{ textAlign: 'center' }}>
          Only CSV files are supported. Max size: 5MB.
        </FormHelperText>

        {fileRejections?.length > 0 ? (
          <div className={classes.rejectedFilesDiv}>
            <h4>Rejected file(s)</h4>
            <ul>{fileRejectionItems}</ul>
          </div>
        ) : null}

        <DialogActions
          style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Button onClick={onClose} color="secondary" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!productCSV && isLoading}
            color="primary"
            autoFocus
          >
            {isLoading ? <CircularProgress /> : 'Confirm Upload'}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
